// src/components/partners/SupplierForm.jsx
import React, { useState, useEffect } from 'react';
import { Form, Card, Alert, Row, Col, InputGroup, Button } from 'react-bootstrap';
import { Save, X, User, Mail, Phone, MapPin, FileText, CreditCard, DollarSign } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createSupplier, getSupplierById, updateSupplier } from '../../api/partner';

const SupplierForm = ({ supplierId, onCancel, onSuccess }) => {
    const isEditMode = !!supplierId;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const initialValues = {
        name: '',
        email: '',
        phone: '',
        address: '',
        paymentTerms: ''
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    useEffect(() => {
        if (isEditMode) {
            fetchSupplierData();
        }
    }, [supplierId]);

    const fetchSupplierData = async () => {
        try {
            setLoading(true);
            const supplierData = await getSupplierById(supplierId);

            setValues({
                name: supplierData.name || '',
                email: supplierData.email || '',
                phone: supplierData.phone || '',
                address: supplierData.address || '',
                paymentTerms: supplierData.paymentTerms || ''
            });
        } catch (err) {
            setError('Impossible de charger les données du client. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = {
        name: {
            required: true,
            errorMessage: 'Le nom du client est obligatoire'
        },
        email: {
            required: false,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Veuillez saisir une adresse email valide'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Valider le formulaire
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const supplierData = {
                ...values
            };

            if (isEditMode) {
                // Mettre à jour le fournisseur existant
                await updateSupplier(supplierId, supplierData);
                setSuccess('Client mis à jour avec succès !');
            } else {
                // Créer un nouveau fournisseur
                await createSupplier(supplierData);
                setSuccess('Client créé avec succès !');
            }

            // Appeler le callback de succès après un court délai pour permettre à l'utilisateur de voir le message de succès
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setValues(prev => ({ ...prev, [name]: checked }));
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier le client' : 'Ajouter un client'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom du client</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <User size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        placeholder="Entrez le nom du client"
                                        required
                                    />
                                </InputGroup>
                                {errors.name && <Form.Text className="text-danger">{errors.name}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Mail size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        placeholder="Entrez l'email du fournisseur"
                                    />
                                </InputGroup>
                                {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Téléphone</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Phone size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={values.phone}
                                        onChange={handleChange}
                                        placeholder="Entrez le numéro de téléphone"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        {/* <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Personne de contact</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="contactPerson"
                                    value={values.contactPerson}
                                    onChange={handleChange}
                                    placeholder="Entrez le nom de la personne de contact"
                                />
                            </Form.Group>
                        </Col> */}
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Adresse</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <MapPin size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                as="textarea"
                                name="address"
                                value={values.address}
                                onChange={handleChange}
                                placeholder="Entrez l'adresse complète"
                                style={{ height: '80px' }}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Row>
                        {/* <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Identifiant fiscal</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <FileText size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="taxIdentification"
                                        value={values.taxIdentification}
                                        onChange={handleChange}
                                        placeholder="Numéro d'identification fiscale"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col> */}

                        {/* <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Coordonnées bancaires</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <CreditCard size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="bankDetails"
                                        value={values.bankDetails}
                                        onChange={handleChange}
                                        placeholder="IBAN ou RIB"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col> */}
                    </Row>

                    {/* <Form.Group className="mb-4">
                        <Form.Label>Conditions de paiement</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <DollarSign size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                name="paymentTerms"
                                value={values.paymentTerms}
                                onChange={handleChange}
                                placeholder="Ex: 30 jours fin de mois"
                            />
                        </InputGroup>
                    </Form.Group> */}
                    <Row>
                        <Col md={6} className="d-flex align-items-center">
                        
                    </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditMode ? 'Mise à jour...' : 'Création...'}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Mettre à jour' : 'Créer'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default SupplierForm;